import { LockOutlineBaseIcon } from '@tableau/icons/LockOutlineBaseIcon';
import { LockUnlockedOutlineIcon } from '@tableau/icons/LockUnlockedOutlineIcon';
import {
  ButtonProps,
  ButtonType,
  CheckBoxWithLabelWidget,
  DialogFooterButtonsWidget,
  DialogPositioningBehavior,
  DialogType,
  DialogWidget,
  InlineAlertType,
  InlineAlertWidget,
  ProgressBarWidget,
  RadioButtonWithLabelGroupWidget,
  RadioButtonWithLabelOption
} from '@tableau/widgets';
import React from 'react';

import {
  Formatters,
  Messages
} from '../../codegen/Localize';
import { AuthorizableType } from '../../permissions/types/AuthorizableType';
import {
  LockUnlockPermissionsState,
  NestedProjectsPermissionsState
} from '../../permissions/utils/LockStatusHelper';
import { PermissionsLockDialogStyles } from '../styles/PermissionsLockDialogStyle';
import { ContainerMessages } from './PermissionsLockUnlock';

const DIALOG_WIDTH = 500;

export const LOCK_DIALOG_TESTID = 'permissions-lock-dialog';
export const LOCK_DIALOG_LOCKED_TESTID = LOCK_DIALOG_TESTID + '-locked';
export const LOCK_DIALOG_UNLOCKED_TESTID = LOCK_DIALOG_TESTID + '-unlocked';
export const LOCK_DIALOG_NESTED_PROJECTS_TESTID = LOCK_DIALOG_TESTID + '-nested-projects';
export const LOCK_DIALOG_WARNING_MESSAGE_TESTID = LOCK_DIALOG_TESTID + '-warning-message';
export const LOCK_DIALOG_FOOTER_TESTID = LOCK_DIALOG_TESTID + '-footer';
export const LOCK_DIALOG_CANCEL_TESTID = LOCK_DIALOG_TESTID + '-cancel';
export const LOCK_DIALOG_SAVE_TESTID = LOCK_DIALOG_TESTID + '-save';
export const PERMISSIONS_PROGRESS_BAR_TESTID = LOCK_DIALOG_TESTID + '-progress-bar';

export enum PERMISSIONS_LOCK_RADIO_INDICIES {
  Mixed = -1, // negative because in the mixed state no radio button will be selected
  Locked = 0,
  Unlocked = 1,
}

const RadioIndexToLockState: { [key: number]: LockUnlockPermissionsState } = Object.freeze({
  [PERMISSIONS_LOCK_RADIO_INDICIES.Mixed]: LockUnlockPermissionsState.Mixed,
  [PERMISSIONS_LOCK_RADIO_INDICIES.Locked]: LockUnlockPermissionsState.Locked,
  [PERMISSIONS_LOCK_RADIO_INDICIES.Unlocked]: LockUnlockPermissionsState.Unlocked,
});
const lockState = (index: PERMISSIONS_LOCK_RADIO_INDICIES):
  LockUnlockPermissionsState => RadioIndexToLockState[index];

const LockStateToRadioIndex: { [key: number]: PERMISSIONS_LOCK_RADIO_INDICIES } = Object.freeze({
  [LockUnlockPermissionsState.Locked]: PERMISSIONS_LOCK_RADIO_INDICIES.Locked,
  [LockUnlockPermissionsState.Unlocked]: PERMISSIONS_LOCK_RADIO_INDICIES.Unlocked,
  [LockUnlockPermissionsState.Mixed]: PERMISSIONS_LOCK_RADIO_INDICIES.Mixed,
});
const lockRadioIndex = (state: LockUnlockPermissionsState):
  PERMISSIONS_LOCK_RADIO_INDICIES => LockStateToRadioIndex[state];

export type PermissionsLockDialogProps = {
  /** The strings to render */
  containerMessages: ContainerMessages;

  /** The number of container authorizables in this dialog. */
  containersNumber: number;

  /** Name of the (first) container authorizable in this dialog. */
  containerName: string;

  /** Type of container authorizable */
  containerAuthorizableType: AuthorizableType;

  /** Callback invoked when the dialog's cancel button is clicked. */
  onCancel: () => void;

  /** Callback invoked when the dialog's save button is clicked.
   * @param lockState the chosen lock state that will be saved
   */
  onSave: (lockState: LockUnlockPermissionsState, nestedProjectsState?: NestedProjectsPermissionsState) => void;

  /** The original lock state of the project(s) when the dialog is first opened */
  initialLockState: LockUnlockPermissionsState;

  /** The original nested project inclusion state of the project(s) when the dialog is first opened */
  initialNestedProjectsState: NestedProjectsPermissionsState;

  /** True if a lock/unlock request is already inflight, in which the save button should be disabled */
  isBusyUpdatingLockStatus: boolean;

  /** The current progress message */
  progressMessage?: string;

  /** The current progress label */
  progressLabel?: string;

  /** The result of the lock status update */
  updateLockStatusResult?: 'success' | 'error' | 'default';
};

export type PermissionsLockDialogState = {
  selectedLockUnlockIndex: number;
  selectedNestedProjectsState: NestedProjectsPermissionsState;
  hasSaveBeenClicked: boolean;
  isBusyUpdatingLockStatus: boolean;
};

type IconFunctionComponent = typeof LockUnlockedOutlineIcon | typeof LockOutlineBaseIcon;

export class PermissionsLockDialog extends React.Component<PermissionsLockDialogProps, PermissionsLockDialogState> {
  private closeButtonRef = React.createRef<HTMLButtonElement>();

  public state: PermissionsLockDialogState = {
    selectedLockUnlockIndex: lockRadioIndex(this.props.initialLockState),
    selectedNestedProjectsState: this.props.initialNestedProjectsState,
    hasSaveBeenClicked: false,
    isBusyUpdatingLockStatus: false,
  };

  public componentDidUpdate(prevProps: this['props']) {
    if (prevProps.isBusyUpdatingLockStatus && !this.props.isBusyUpdatingLockStatus) {
      // focus the close button when the save operation completes
      this.closeButtonRef.current?.focus();
    }
  }

  private handleChangeLockOption = (lockState: LockUnlockPermissionsState, checkedIndex: number): void => {
    // when changing to locked, nested projects included is the default
    this.setState((prevState) => {
      return {
        selectedLockUnlockIndex: checkedIndex,
        selectedNestedProjectsState: lockState === LockUnlockPermissionsState.Locked ?
          NestedProjectsPermissionsState.Included :
          prevState.selectedNestedProjectsState,
      };
    });
  };

  private handleChangeNestedProjectsOption = (checked: boolean): void => {
    this.setState({ selectedNestedProjectsState: checked ? NestedProjectsPermissionsState.Included : NestedProjectsPermissionsState.Excluded });
  };

  private shouldEnableNestedProjectsOption = (): boolean => {
    return this.props.containerAuthorizableType === AuthorizableType.PROJECT;
  };

  private willLockContainers = (): boolean => {
    return (this.props.initialLockState !== LockUnlockPermissionsState.Locked)
      && (this.state.selectedLockUnlockIndex === PERMISSIONS_LOCK_RADIO_INDICIES.Locked);
  };

  private isSaveDisabled = (isOperationComplete: boolean): boolean => {
    // disabled if the current selection wouldn't result in a change to the lock status
    // or if an operation is in progress
    const isLockStateSame = lockState(this.state.selectedLockUnlockIndex) === this.props.initialLockState;
    const isNestedProjectApplicable = this.state.selectedLockUnlockIndex === LockUnlockPermissionsState.Locked;
    const isNestedProjectsSame = this.shouldEnableNestedProjectsOption() ?
      this.state.selectedNestedProjectsState === this.props.initialNestedProjectsState :
      true; // if no locking subprojects, only consider lock state, not nested projects state
    return isOperationComplete || (isLockStateSame && (!isNestedProjectApplicable || isNestedProjectsSame)) ||
      this.props.isBusyUpdatingLockStatus;
  };

  private handleSave = (): void => {
    // nested projects state is ignored (basically default to excluded) if saving unlocked
    const lockOptionToSave: LockUnlockPermissionsState = lockState(this.state.selectedLockUnlockIndex);
    const nestedProjectsOptionToSave: NestedProjectsPermissionsState = lockOptionToSave === LockUnlockPermissionsState.Unlocked ? NestedProjectsPermissionsState.Excluded : this.state.selectedNestedProjectsState;
    this.setState({ hasSaveBeenClicked: true });
    this.props.onSave(lockOptionToSave, nestedProjectsOptionToSave);
  };

  private showWarningIncludingNested = (): boolean => {
    return this.shouldEnableNestedProjectsOption() && this.state.selectedNestedProjectsState === NestedProjectsPermissionsState.Included && this.props.containerMessages.warningIncludingNestedSingle !== undefined;
  };

  private handleCancel = (): void => {
    // Reset state before closing
    this.setState({
      selectedLockUnlockIndex: lockRadioIndex(this.props.initialLockState),
      selectedNestedProjectsState: this.props.initialNestedProjectsState,
      hasSaveBeenClicked: false,
    });
    this.props.onCancel();
  };

  private renderWarningMessage = (): JSX.Element => {
    let warningMessage;
    if (this.props.containersNumber === 1) {
      warningMessage = this.showWarningIncludingNested() ?
        this.props.containerMessages.warningIncludingNestedSingle({CONTAINER_NAME: this.props.containerName}) :
        this.props.containerMessages.warningSingle({CONTAINER_NAME: this.props.containerName});
    } else {
       warningMessage = this.showWarningIncludingNested() ?
        this.props.containerMessages.warningIncludingNestedMultiple({
          CONTAINER_COUNT: this.props.containersNumber,
          CONTAINER_COUNT_LOC: Formatters.formatCount(this.props.containersNumber)
        }) :
        this.props.containerMessages.warningMultiple({
          CONTAINER_COUNT: this.props.containersNumber,
          CONTAINER_COUNT_LOC: Formatters.formatCount(this.props.containersNumber)
        });
    }
    return (
      <InlineAlertWidget type={InlineAlertType.Info} className={PermissionsLockDialogStyles.WarningMessageContainer}>
        <span data-tb-test-id={LOCK_DIALOG_WARNING_MESSAGE_TESTID}>
          {warningMessage}
        </span>
      </InlineAlertWidget>
    );
  };

  private renderProgressBar = (): JSX.Element | null => {
    if (!this.state.hasSaveBeenClicked || (!this.props.isBusyUpdatingLockStatus && !this.props.updateLockStatusResult)) {
      return null;
    }

    return (
      <div className={PermissionsLockDialogStyles.ProgressBarContainer} >
        <ProgressBarWidget
          className={PermissionsLockDialogStyles.ProgressBar}
          data-tb-test-id={PERMISSIONS_PROGRESS_BAR_TESTID}
          kind={this.props.updateLockStatusResult}
          label={this.props.progressLabel}
          message={this.props.progressMessage}
          style={{ width: '100%' }}
        />
      </div>
    );
  };

  private renderFooter = (): JSX.Element => {
    const isOperationComplete = this.props.updateLockStatusResult === 'success' || this.props.updateLockStatusResult === 'error';
    const isOperationInProgress = this.props.isBusyUpdatingLockStatus;

    const buttons: Array<ButtonProps> = [
      {
        buttonRef: this.closeButtonRef,
        buttonType: ButtonType.Outline,
        children: isOperationComplete ? Messages.close() : Messages.cancel(),
        disabled: isOperationInProgress,
        handleClick: this.handleCancel,
        testId: LOCK_DIALOG_CANCEL_TESTID,
      },
      {
        buttonType: ButtonType.Primary,
        children: Messages.save(),
        disabled: this.isSaveDisabled(isOperationComplete),
        handleClick: this.handleSave,
        showSpinner: isOperationInProgress,
        testId: LOCK_DIALOG_SAVE_TESTID,
      },
    ];

    return (
      <DialogFooterButtonsWidget
        buttons={buttons}
        testId={LOCK_DIALOG_FOOTER_TESTID}
      />
    );
  };

  private renderLockDialogRadioButtonLabel = (labelName: string, labelDescription: string, Icon: IconFunctionComponent): JSX.Element => {
    /* eslint-disable jsx-a11y/alt-text */
    return (
      <span>
        <Icon alt="" size={14} className={PermissionsLockDialogStyles.StatusIcon} />
        <span className={PermissionsLockDialogStyles.RadioButtonLabel}>
          {labelName}
        </span>
        <span className={PermissionsLockDialogStyles.PermissionOptionDescription}>
          {labelDescription}
        </span>
      </span>
    );
  };

  private renderLockedRadioButtonContent = (): JSX.Element => {
    return (
      <div className={PermissionsLockDialogStyles.RadioButtonLabelContainer}>
        {this.shouldEnableNestedProjectsOption() && this.renderNestedProjectsOption()}
        {this.willLockContainers() && this.renderWarningMessage()}
      </div>
    );
  };

  private renderUnlockedRadioButtonLabel = (): JSX.Element => {
    return this.renderLockDialogRadioButtonLabel(
      Messages.lockContainerPermissions_unlockHeader(),
      this.props.containerMessages.unlockedRadioButtonLabel(),
      LockUnlockedOutlineIcon
    );
  };

  private renderLockUnlockRadioButtons = (groupLabel: string): JSX.Element => {
    const lockingRadioButtons: RadioButtonWithLabelOption<LockUnlockPermissionsState>[] = [
      {
        label:
          this.renderLockDialogRadioButtonLabel(
            Messages.lockContainerPermissions_lockHeader(),
            this.props.containerMessages.lockedRadioButtonLabel(),
            LockOutlineBaseIcon
          ),
        detail: this.renderLockedRadioButtonContent(),
        testId: LOCK_DIALOG_LOCKED_TESTID,
        value: LockUnlockPermissionsState.Locked,
        disabled: this.props.isBusyUpdatingLockStatus || this.state.hasSaveBeenClicked,
      },
      {
        label: this.renderUnlockedRadioButtonLabel(),
        testId: LOCK_DIALOG_UNLOCKED_TESTID,
        value: LockUnlockPermissionsState.Unlocked,
        disabled: this.props.isBusyUpdatingLockStatus || this.state.hasSaveBeenClicked,
      }
    ];

    return (
      <RadioButtonWithLabelGroupWidget
        aria-label={groupLabel}
        buttonList={lockingRadioButtons}
        checkedIndex={this.state.selectedLockUnlockIndex}
        handleChange={this.handleChangeLockOption}
        removeLeftMargin
      />
    );
  };

  private renderNestedProjectsOption = (): JSX.Element => {
    return (
      <div className={PermissionsLockDialogStyles.NestedProjectsOption}>
        <CheckBoxWithLabelWidget
          checked={this.state.selectedNestedProjectsState === NestedProjectsPermissionsState.Included}
          disabled={this.state.selectedLockUnlockIndex !== PERMISSIONS_LOCK_RADIO_INDICIES.Locked ||
                   this.props.isBusyUpdatingLockStatus ||
                   this.state.hasSaveBeenClicked}
          handleChange={this.handleChangeNestedProjectsOption}
          indeterminate={this.state.selectedNestedProjectsState === NestedProjectsPermissionsState.Mixed}
          label={Messages.lockProjectPermissions_nestedProjectsOption()}
          removeLeftMargin
          testId={LOCK_DIALOG_NESTED_PROJECTS_TESTID}
        />
      </div>
    );
  };

  public render(): JSX.Element {
    const title = this.props.containersNumber === 1 ?
      this.props.containerMessages.titleSingle() :
      this.props.containerMessages.titleMultiple({
        CONTAINER_COUNT: this.props.containersNumber,
        CONTAINER_COUNT_LOC: Formatters.formatCount(this.props.containersNumber)
      });

    const isOperationInProgress = this.props.isBusyUpdatingLockStatus;

    return (
      <DialogWidget
        dialogWidth={DIALOG_WIDTH}
        isModal={true}
        onCloseRequest={isOperationInProgress ? undefined : this.handleCancel}
        positioningBehavior={DialogPositioningBehavior.Centered}
        showCloseButton={false} // there's a Cancel/Close button in the footer
        showFooter={true}
        footerContent={this.renderFooter()}
        handleGlassClick={isOperationInProgress ? undefined : this.handleCancel}
        testId={LOCK_DIALOG_TESTID}
        title={title}
        type={DialogType.NormalWithTitleBar}
        dialogStyle={{ zIndex: 20 }}
      >
        {this.renderLockUnlockRadioButtons(title)}
        {this.renderProgressBar()}
      </DialogWidget>
    );
  }
}
